import { useEffect, useState, lazy, Suspense } from "react";

import { useLocation, Routes, Route, Navigate } from "react-router-dom";

import { useSelector } from "react-redux";

import clinicAPI from "../../../services/clinicApi";

import ClinicLayout from "../ClinicLayout";
import AdminAppShell from "../AdminPortal/AdminAppShell";
import ClubPortalBrandingActivator from "../ClubPortalBrandingActivator";
import AdminAppMore from "../AdminPortal/AdminAppMore";
import AdminOwnerProfile from "../AdminPortal/AdminOwnerProfile";

import AdminOverview from "../AdminOverview";

import AdminSettings from "../AdminSettings";

import AdminPatientsRoutes from "../AdminPatients";

import AdminDoctorsRoutes from "../AdminDoctors";
import AdminTeamRoutes from "../AdminTeam";

import L from "../../../constants/baysportLabels";

import {

  redirectForHiddenAdminRoute,

  resolveAdminTabId,

} from "../../../utils/adminNav";
import { hasAdminAccess } from "../../../utils/roles";
import { useIsAdminApp } from "../../../hooks/useStandalone";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { ToastProvider } from "../../ui";
import {
  TrainingModeProvider,
  TrainingChrome,
} from "../../../training";
import {
  ActivationProvider,
  ActivationChrome,
  QuickStartWelcomeModal,
  QuickStartDoneModal,
  useActivation,
} from "../../../activation";
import {
  isQuickStartFeatureOn,
  isHelpEnabled,
} from "../../../utils/productFlags";

const AdminAppointments = lazy(() => import("../AdminAppointments"));
const AdminSchedule = lazy(() => import("../AdminSchedule"));
const AdminVenues = lazy(() => import("../AdminVenues"));
const AdminDirections = lazy(() => import("../AdminDirections"));
const AdminGroups = lazy(() => import("../AdminGroups"));
const AdminMemberships = lazy(() => import("../AdminMemberships"));
const AdminFinance = lazy(() => import("../AdminFinance"));
const AdminAnalytics = lazy(() => import("../AdminAnalytics"));
const AdminSalesFunnel = lazy(() => import("../AdminSalesFunnel"));
const AdminModules = lazy(() => import("../AdminModules"));
const ClubWebsiteEditor = lazy(() => import("../ClubWebsite/ClubWebsiteEditor"));
const AdminClubLife = lazy(() => import("../AdminClubLife"));
const AdminNotifications = lazy(() => import("../AdminNotifications"));
const AdminJournal = lazy(() => import("../AdminJournal"));
const AdminPolls = lazy(() => import("../AdminPolls"));
const AdminChatRoutes = lazy(() => import("../AdminPortal/AdminChatRoutes"));
const AdminHelp = lazy(() => import("../AdminHelp"));
const AdminPartners = lazy(() => import("../AdminPartners"));

function TabFallback() {
  return null;
}

function OnboardingChrome() {
  const { visible: activationVisible } = useActivation();
  if (!isQuickStartFeatureOn()) return null;
  return (
    <>
      {!activationVisible ? <TrainingChrome /> : null}
      <ActivationChrome />
      <QuickStartWelcomeModal />
      <QuickStartDoneModal />
    </>
  );
}

function AdminDashboardContent({ appMode = false, isMobile = false }) {
  const initialized = useSelector((state) => state.init.initialized);

  const isAuth = useSelector((state) => state.auth.isAuth);

  const { tenant, club, billing, entitlements, role } = useSelector((state) => state.auth);

  const location = useLocation();

  const tab = resolveAdminTabId(location.pathname);



  const [doctors, setDoctors] = useState([]);



  useEffect(() => {

    if (!initialized || !isAuth) return;

    clinicAPI

      .getDoctors({ include_summary: "false" })

      .then((r) => setDoctors(r.doctors || []))

      .catch(() => setDoctors([]));

  }, [initialized, isAuth]);



  const hiddenRedirect = redirectForHiddenAdminRoute(
    location.pathname,
    club,
    tenant,
    location.search,
    { billing, entitlements, role },
  );

  if (hiddenRedirect) {

    return <Navigate to={hiddenRedirect} replace />;

  }



  if (

    location.pathname === "/manage/app/patients" ||

    location.pathname.startsWith("/manage/app/patients/")

  ) {

    return (

      <Navigate

        to={location.pathname.replace("/patients", "/clients")}

        replace

      />

    );

  }



  if (

    location.pathname === "/manage/app/doctors" ||

    location.pathname.startsWith("/manage/app/doctors/")

  ) {

    return (

      <Navigate

        to={location.pathname.replace("/doctors", "/trainers")}

        replace

      />

    );

  }



  if (

    location.pathname === "/manage/app/branches" ||

    location.pathname.startsWith("/manage/app/branches/")

  ) {

    return <Navigate to="/manage/app/venues" replace />;

  }



  if (

    location.pathname === "/manage/app/services" ||

    location.pathname.startsWith("/manage/app/services/")

  ) {

    return <Navigate to="/manage/app/directions" replace />;

  }



  return (

    <Suspense fallback={<TabFallback />}>

      {tab === "overview" && (
        <AdminOverview tenant={tenant} appMode={appMode && isMobile} />
      )}

      {tab === "more" && <AdminAppMore />}

      {tab === "profile" && (
        <Routes>
          <Route path="profile/*" element={<AdminOwnerProfile />} />
        </Routes>
      )}



      {tab === "chat" && <AdminChatRoutes />}

      {tab === "groups" && <AdminGroups />}



      {tab === "memberships" && <AdminMemberships />}



      {tab === "finance" && <AdminFinance />}

      {tab === "life" && <AdminClubLife />}

      {tab === "notifications" && <AdminNotifications />}

      {tab === "polls" && <AdminPolls />}

      {tab === "analytics" && <AdminAnalytics />}

      {tab === "sales" && <AdminSalesFunnel />}

      {tab === "modules" && (
        <Routes>
          <Route path="modules/*" element={<AdminModules />} />
        </Routes>
      )}

      {tab === "website" && <ClubWebsiteEditor />}

      {tab === "team" && (

        <Routes>

          <Route path="team/*" element={<AdminTeamRoutes />} />

        </Routes>

      )}

      {tab === "trainers" && (

        <Routes>

          <Route path="trainers/*" element={<AdminDoctorsRoutes />} />

        </Routes>

      )}



      {tab === "directions" && <AdminDirections />}



      {tab === "venues" && <AdminVenues />}



      {tab === "schedule" && <AdminSchedule doctors={doctors} />}

      {tab === "journal" && <AdminJournal />}

      {tab === "appointments" && <AdminAppointments />}



      {tab === "clients" && (

        <Routes>

          <Route path="clients/*" element={<AdminPatientsRoutes />} />

        </Routes>

      )}



      {tab === "settings" && <AdminSettings />}

      {tab === "help" && isHelpEnabled() ? <AdminHelp /> : null}

      {tab === "partners" && <AdminPartners />}

    </Suspense>

  );

}



export default function AdminDashboard() {
  const { tenant, role } = useSelector((state) => state.auth);
  const location = useLocation();
  const isAdminApp = useIsAdminApp(hasAdminAccess(role));
  const isMobile = useIsMobile(768);
  const tab = resolveAdminTabId(location.pathname);
  const isToday = tab === "overview";

  const content = (
    <>
      <AdminDashboardContent appMode={isAdminApp} isMobile={isMobile} />
    </>
  );

  // CMS сайта — без оболочки админки (полноэкранный редактор)
  if (tab === "website") {
    return (
      <ToastProvider>
        <Suspense fallback={<TabFallback />}>
          <ClubWebsiteEditor />
        </Suspense>
      </ToastProvider>
    );
  }


  if (isAdminApp) {
    return (
      <ToastProvider>
        <TrainingModeProvider>
          <ActivationProvider>
            <AdminAppShell>{content}</AdminAppShell>
            <OnboardingChrome />
          </ActivationProvider>
        </TrainingModeProvider>
      </ToastProvider>
    );
  }

  return (
    <TrainingModeProvider>
      <ActivationProvider>
        <ClinicLayout
          title={isToday ? L.summary : L.adminTitle}
          subtitle={isToday ? tenant?.name || L.club : tenant?.name || ""}
          settingsQuickLink={isToday}
        >
          <ClubPortalBrandingActivator />
          {content}
        </ClinicLayout>
        <OnboardingChrome />
      </ActivationProvider>
    </TrainingModeProvider>
  );
}


